<template>
  <div v-if="hasEquivalents">
    <Modal
      :dismissible="true"
      :show-footer="false"
      content-padding="0px"
      position="center"
      @hide="resetState"
    >
      <template #activator="{ show }">
        <button
          :disabled="isLoading"
          :aria-busy="isLoading"
          aria-haspopup="dialog"
          aria-label="Open equivalent review extractions"
          class="group/eqbtn flex h-8 items-center justify-center rounded-md border border-blue-800 bg-indigo-50 p-1.5 text-blue-800 transition-all duration-300 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
          type="button"
          @click="fetchAndShow(show)"
        >
          <i-material-symbols-content-copy-outline class="size-4 shrink-0" />
          <span
            class="max-w-0 overflow-hidden whitespace-nowrap pl-0 text-sm font-medium opacity-0 transition-all duration-300 group-hover/eqbtn:max-w-[280px] group-hover/eqbtn:pl-1.5 group-hover/eqbtn:opacity-100"
          >
            Use previous extractions
          </span>
        </button>
      </template>
      <template #header="{ hide }">
        <div class="flex w-full items-center justify-between gap-4">
          <div>
            <p class="text-lg font-semibold text-dark-neutral-900">Copy from equivalent review</p>
            <p class="text-sm font-normal text-mid-neutral-500">{{ subtitle }}</p>
          </div>
          <button
            aria-label="Close copy from equivalent dialog"
            class="flex size-[30px] items-center justify-center rounded-full bg-light-neutral-500 text-mid-neutral-500"
            @click="hide"
          >
            <i-material-symbols-close aria-hidden="true" class="size-4" />
          </button>
        </div>
      </template>
      <template #content="{ hide }">
        <div class="flex w-[650px] flex-col overflow-hidden rounded-b-lg" :aria-busy="isLoading">
          <div
            v-if="isLoading"
            class="flex h-[32rem] items-center justify-center p-10"
            role="status"
          >
            <i-material-symbols-progress-activity
              aria-hidden="true"
              class="size-6 animate-spin text-primary-600"
            />
          </div>
          <div
            v-else-if="allStudies.length === 0"
            class="flex h-[32rem] items-center justify-center px-4 py-10 text-center text-sm text-gray-500"
            role="status"
          >
            No equivalent extractions found
          </div>
          <div v-else class="relative flex h-[38rem] flex-col">
            <div
              v-if="showConfirm"
              class="absolute inset-0 z-20 flex items-center justify-center bg-gray-900/40 backdrop-blur-[2px]"
            >
              <div
                class="w-[400px] overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5"
              >
                <div class="px-6 pb-6 pt-8">
                  <div class="flex flex-col items-center text-center">
                    <div
                      class="mb-5 flex size-12 items-center justify-center rounded-full bg-amber-100 ring-4 ring-amber-50"
                    >
                      <i-material-symbols-warning-rounded class="size-6 text-amber-600" />
                    </div>
                    <h3 class="text-lg font-semibold text-gray-900">Replace current extraction?</h3>
                    <p class="mt-2 text-sm leading-relaxed text-gray-500">
                      This will replace the current value with the extraction from the selected
                      review.
                    </p>
                  </div>
                </div>
                <div class="flex flex-row-reverse gap-3 bg-gray-50 px-6 py-4">
                  <ActionButton
                    class="flex-1 justify-center"
                    color="primary"
                    @click="executeCopy(hide)"
                  >
                    Replace
                  </ActionButton>
                  <ActionButton
                    class="flex-1 justify-center"
                    color="neutral"
                    @click="showConfirm = false"
                  >
                    Cancel
                  </ActionButton>
                </div>
              </div>
            </div>

            <div class="flex flex-1 overflow-hidden border-t border-gray-200">
              <nav class="flex w-1/3 shrink-0 flex-col border-r border-gray-200 bg-gray-50/30 py-4">
                <button
                  class="flex w-full items-center gap-3 border-l-[3px] px-4 py-3 text-left transition-colors"
                  :class="[
                    currentStep === 1
                      ? 'border-blue-800 bg-blue-50'
                      : 'border-transparent cursor-pointer hover:bg-gray-100/50',
                  ]"
                  @click="goToStep(1)"
                >
                  <span
                    class="flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-medium"
                    :class="
                      currentStep === 1 ? 'bg-blue-800 text-white' : 'bg-blue-100 text-blue-800'
                    "
                  >
                    <i-material-symbols-check-rounded v-if="currentStep > 1" class="size-4" />
                    <span v-else>1</span>
                  </span>

                  <span
                    class="text-sm font-medium"
                    :class="currentStep === 1 ? 'font-semibold text-blue-800' : 'text-gray-700'"
                  >
                    Review
                  </span>
                </button>

                <button
                  class="flex w-full items-center gap-3 border-l-[3px] px-4 py-3 text-left transition-colors"
                  :class="[
                    currentStep === 2
                      ? 'border-blue-800 bg-blue-50'
                      : selectedStudy
                        ? 'border-transparent cursor-pointer hover:bg-gray-100/50'
                        : 'border-transparent cursor-not-allowed opacity-50',
                  ]"
                  :disabled="!selectedStudy"
                  @click="goToStep(2)"
                >
                  <span
                    class="flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-medium"
                    :class="
                      currentStep === 2 ? 'bg-blue-800 text-white' : 'bg-blue-100 text-blue-800'
                    "
                  >
                    2
                  </span>
                  <span
                    class="text-sm font-medium"
                    :class="currentStep === 2 ? 'font-semibold text-blue-800' : 'text-gray-700'"
                  >
                    Extraction
                  </span>
                </button>
              </nav>

              <div class="flex min-w-0 flex-1 flex-col bg-white">
                <div
                  v-if="currentStep === 1"
                  class="flex flex-1 flex-col gap-2 overflow-y-auto p-4"
                >
                  <button
                    v-for="review in reviews"
                    :key="review.name"
                    class="flex w-full items-center rounded-lg px-4 py-3 text-left text-sm transition-colors"
                    :class="
                      selectedReview?.name === review.name
                        ? 'border border-blue-800 ring-1 ring-blue-800 ring-inset bg-blue-50'
                        : 'border border-transparent hover:bg-gray-50'
                    "
                    @click="selectReview(review)"
                  >
                    <span
                      class="flex-1 truncate font-medium"
                      :class="
                        selectedReview?.name === review.name ? 'text-blue-800' : 'text-gray-800'
                      "
                    >
                      {{ review.name }}
                    </span>
                  </button>
                </div>
                <div
                  v-else-if="currentStep === 2 && selectedStudy"
                  class="flex h-full min-h-0 flex-col"
                >
                  <div class="flex flex-1 flex-col overflow-y-auto p-4">
                    <button
                      v-for="extraction in selectedStudy.extractions"
                      :key="extraction.attributeStructureId"
                      class="flex w-full items-center rounded-lg px-4 py-3 text-left text-sm transition-colors"
                      :class="
                        selectedExtraction?.attributeStructureId === extraction.attributeStructureId
                          ? 'border border-blue-800 ring-1 ring-blue-800 ring-inset bg-blue-50'
                          : 'border border-transparent hover:bg-gray-50'
                      "
                      @click="selectedExtraction = extraction"
                    >
                      <span
                        class="flex-1 truncate font-medium"
                        :class="
                          selectedExtraction?.attributeStructureId ===
                          extraction.attributeStructureId
                            ? 'text-blue-800'
                            : 'text-gray-800'
                        "
                      >
                        {{ extraction.label }}
                      </span>
                    </button>
                  </div>
                  <div class="flex h-1/2 flex-col border-t border-primary-100 bg-blue-50">
                    <p
                      class="shrink-0 px-6 pt-4 text-xs font-semibold uppercase tracking-wider text-primary-700"
                    >
                      Preview
                    </p>
                    <div class="flex-1 overflow-y-auto px-6 pb-6 pt-2">
                      <template v-if="selectedExtraction">
                        <h3 class="mb-1 text-base font-bold text-primary-900">
                          {{ selectedExtraction.label }}
                        </h3>
                        <slot name="preview" :extraction="selectedExtraction" />
                      </template>
                      <div
                        v-else
                        class="flex h-full items-center justify-center text-sm italic text-gray-400"
                      >
                        Select an extraction to preview
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <footer
              class="flex items-center justify-between border-t border-gray-200 bg-white px-6 py-4"
            >
              <p class="min-w-0 flex-1 truncate pr-4 text-sm text-gray-500" :title="footerText">
                {{ footerText }}
              </p>
              <div class="flex shrink-0 items-center gap-3">
                <ActionButton color="neutral" @click="hide"> Cancel </ActionButton>
                <ActionButton color="primary" :disabled="!selectedExtraction" @click="requestCopy">
                  Copy Extraction
                </ActionButton>
              </div>
            </footer>
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts" generic="T">
import { ref, shallowRef, computed, onMounted } from "vue";
import Modal from "@app/components/Global/Modal/Modal.vue";
import ActionButton from "@app/components/Global/ActionButton.vue";

export type EquivalentExtraction<V> = {
  label: string;
  attributeStructureId: string;
  value?: V;
};

export type EquivalentStudy<V> = {
  id: number;
  title?: string;
  extractions: EquivalentExtraction<V>[];
};

export type EquivalentReview<V> = {
  name: string;
  studies: EquivalentStudy<V>[];
};

export type EquivalentStudyEntry<V> = {
  reviewSeriesName: string;
  study: EquivalentStudy<V>;
};

const props = defineProps<{
  subtitle: string;
  cacheKey: string | number;
  fetchStudies: () => Promise<EquivalentStudyEntry<T>[]>;
}>();

const emit = defineEmits<{
  copy: [value: T];
}>();

const isLoading = ref(false);
const currentStep = ref(1);
const showConfirm = ref(false);
const allStudies = shallowRef<EquivalentStudyEntry<T>[]>([]);
const selectedReview = shallowRef<EquivalentReview<T> | null>(null);
const selectedStudy = shallowRef<EquivalentStudy<T> | null>(null);
const selectedExtraction = shallowRef<EquivalentExtraction<T> | null>(null);
const hasEquivalents = computed(() => allStudies.value.length > 0);
const studiesCache = new Map<string | number, EquivalentStudyEntry<T>[]>();
const pendingRequests = new Map<string | number, Promise<EquivalentStudyEntry<T>[]>>();

onMounted(() => {
  fetchEquivalents();
});

const reviews = computed<EquivalentReview<T>[]>(() => {
  const grouped = new Map<string, EquivalentStudy<T>[]>();
  for (const entry of allStudies.value) {
    const existing = grouped.get(entry.reviewSeriesName);
    if (existing) {
      existing.push(entry.study);
    } else {
      grouped.set(entry.reviewSeriesName, [entry.study]);
    }
  }
  return Array.from(grouped, ([name, studies]) => ({ name, studies }));
});

const footerText = computed(() => {
  const parts = [];
  if (selectedReview.value) parts.push(selectedReview.value.name);
  if (selectedStudy.value)
    parts.push(selectedStudy.value.title || `Study #${selectedStudy.value.id}`);
  return parts.join(" · ");
});

function resetState() {
  selectedReview.value = null;
  selectedStudy.value = null;
  selectedExtraction.value = null;
  currentStep.value = 1;
  showConfirm.value = false;
}

function goToStep(step: number) {
  if (step === 1) {
    currentStep.value = 1;
  } else if (step === 2 && selectedStudy.value) {
    currentStep.value = 2;
  }
}

async function fetchEquivalents() {
  const cacheKey = props.cacheKey;

  const cachedStudies = studiesCache.get(cacheKey);
  if (cachedStudies) {
    allStudies.value = cachedStudies;
    return;
  }

  isLoading.value = true;
  try {
    let request = pendingRequests.get(cacheKey);
    if (!request) {
      request = props.fetchStudies();
      pendingRequests.set(cacheKey, request);
    }

    const studies = await request;
    studiesCache.set(cacheKey, studies);
    allStudies.value = studies;
  } catch {
    allStudies.value = [];
  } finally {
    pendingRequests.delete(cacheKey);
    isLoading.value = false;
  }
}

function fetchAndShow(show: () => void) {
  show();
}

function selectReview(review: EquivalentReview<T>) {
  selectedReview.value = review;
  selectedStudy.value = review.studies[0] ?? null;
  selectedExtraction.value = null;
  currentStep.value = 2;
}

function requestCopy() {
  showConfirm.value = true;
}

function executeCopy(hide: () => void) {
  if (selectedExtraction.value?.value) {
    emit("copy", selectedExtraction.value.value);
  }
  hide();
}
</script>
